/**
 * Политика конфиденциальности и согласие на обработку персональных данных.
 *
 * Текст типовой и написан под то, что проект действительно делает с данными.
 * Он не заменяет проверку юристом: перед публичным запуском его нужно
 * вычитать и указать реального оператора с контактными данными.
 *
 * Версия должна совпадать с PRIVACY_POLICY_VERSION на сервере: именно она
 * сохраняется у пользователя вместе с датой согласия.
 *
 * Сам текст живёт в словарях перевода, а страница — только его разметка:
 * юридический документ переводится целиком, кусками по одному абзацу его
 * собирать неудобно и легко потерять смысл.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAuthSettings } from "../api/queries";
import { usePageMeta } from "../lib/usePageMeta";

const CONTACT_EMAIL = "privacy@chronoguessr.io";
const LINK = "text-amber-300 underline underline-offset-2 hover:text-amber-200";

interface PolicySection {
  /** Якорь для ссылок прямо на раздел, например из уведомления о cookie. */
  id?: string;
  title: string;
  text?: string[];
  items?: string[];
  note?: string;
}

export function PrivacyPage() {
  const { t } = useTranslation();
  usePageMeta({ title: t("privacy.title") });
  const settings = useAuthSettings();
  const version = settings.data?.privacyPolicyVersion;

  const sections = t("privacy.sections", { returnObjects: true }) as PolicySection[];

  // Браузер сам прокручивает к якорю только при загрузке страницы целиком,
  // а сюда приходят переходом внутри приложения — делаем это сами.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: "smooth" });
  }, [hash]);

  return (
    <div className="mx-auto max-w-3xl py-8">
      <div className="panel p-7">
        <h1 className="font-sans text-4xl text-ink-50">{t("privacy.title")}</h1>
        <p className="mt-2 text-sm text-ink-500">
          {t("privacy.subtitle")}
          {version && <> · {t("privacy.version", { version })}</>}
        </p>

        <div className="mt-8 space-y-8 text-sm leading-relaxed text-ink-300">
          {sections.map((section) => (
            <section key={section.title} id={section.id} className="scroll-mt-24">
              <h2 className="mb-2 font-sans text-xl text-ink-50">{section.title}</h2>

              {section.text?.map((paragraph) => (
                <p key={paragraph} className="mb-3 last:mb-0">
                  <WithEmail text={paragraph} />
                </p>
              ))}

              {section.items && (
                <ul className="space-y-1.5">
                  {section.items.map((item) => (
                    <li key={item} className="flex gap-2.5">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-amber-400/70" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.note && (
                <p className="mt-3">
                  <WithEmail text={section.note} />
                </p>
              )}
            </section>
          ))}
        </div>

        <p className="mt-8 rounded-xl border border-ink-800 bg-ink-850/60 p-4 text-xs text-ink-500">
          {t("privacy.disclaimer")}
        </p>
      </div>
    </div>
  );
}

/**
 * Превращает адрес оператора в ссылку прямо посреди абзаца.
 *
 * Разметка внутри переводимой строки быстро превращается в источник ошибок,
 * поэтому текст остаётся обычным, а адрес узнаётся по совпадению.
 */
function WithEmail({ text }: { text: string }) {
  const parts = text.split(CONTACT_EMAIL);
  if (parts.length === 1) return <>{text}</>;

  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>
          {part}
          {index < parts.length - 1 && (
            <a href={`mailto:${CONTACT_EMAIL}`} className={LINK}>
              {CONTACT_EMAIL}
            </a>
          )}
        </span>
      ))}
    </>
  );
}
